import {
    ACTIVE_BG,
    BOTTOM_ITEMS,
    COMPANY_NAME,
    NAV_GROUPS,
    SIDEBAR_BG,
    type MenuItem,
} from '@constants/sidebar';
import {
    isModuleLocked,
    PREMIUM_GROUP_LABEL,
    PREMIUM_TOOLTIP,
} from '@constants/lockedModules';
import { useLeftSidebar } from '@hooks/sidebar/useLeftSidebar';
import { Box, Stack, Text, Menu, UnstyledButton } from '@mantine/core';
import {
    CaretDown,
    CaretLeft,
    CaretRight,
    Lock,
    SignOut,
    UserCircle,
} from '@phosphor-icons/react';

import React, { useEffect, useState, type FC, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import HoverTooltip from '@components/common/HoverTooltip';
import { clearMerchantIdCache } from '@api';
import { getInternalWarehouseCount } from '@api/inventory';
import { getOrgConfig } from '@api/organization';
import { setDisplayCurrency } from '@utils/currency';
import { clearAllAiThreadCookies } from '../../providers/AiAnalyst/useAiChatThreadPersistence';
import { clearAllAgentSessionStorage } from '@hooks/useAgentChat';
import { clearQuickHelpSessionStorage } from '@providers/QuickHelp/QuickHelpProvider';

const getUserFromStorage = (): {
    fullName: string;
    organizationId: string | null;
} => {
    try {
        const userJson = localStorage.getItem('user');
        if (userJson) {
            const user = JSON.parse(userJson);
            const firstName = user.firstName || '';
            const lastName = user.lastName || '';
            return {
                fullName: `${firstName} ${lastName}`.trim() || 'User',
                organizationId: user.organizationId || null,
            };
        }
    } catch {
        // ignore
    }
    return { fullName: 'User', organizationId: null };
};

const SideNavigationBar: FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const currentPath = location.pathname;

    const sidebarCtx = useLeftSidebar();
    const isOpen = sidebarCtx?.isOpen ?? true;
    const toggleSidebar = sidebarCtx?.toggle ?? (() => {});
    const collapsed = !isOpen;
    const [settingsOpen, setSettingsOpen] = useState(false);
    // Premium lists start folded; a section's lock row opens it. Keyed by section label.
    const [premiumOpen, setPremiumOpen] = useState<Record<string, boolean>>({});
    const togglePremium = (key: string) =>
        setPremiumOpen((prev) => ({ ...prev, [key]: !prev[key] }));

    const { fullName: userFullName, organizationId } = useMemo(
        () => getUserFromStorage(),
        [],
    );

    const { data: internalWarehouseCount } = useQuery({
        queryKey: ['internal-warehouse-count'],
        queryFn: getInternalWarehouseCount,
        staleTime: 5 * 60 * 1000,
    });
    const canRebalance =
        internalWarehouseCount === undefined || internalWarehouseCount > 1;

    const { data: orgConfig } = useQuery({
        queryKey: ['org-config', organizationId],
        queryFn: () => getOrgConfig(organizationId!),
        enabled: !!organizationId,
        staleTime: 5 * 60 * 1000,
    });
    const warehouseOpsEnabled = orgConfig?.warehouseOpsEnabled ?? false;

    // Seed the module-level display currency as soon as the org config lands, so every
    // money formatter in the app (including non-hook call sites) renders the right symbol.
    useEffect(() => {
        setDisplayCurrency(orgConfig?.currency);
    }, [orgConfig?.currency]);

    // Each section splits into what the plan includes and what it does not. A feature the
    // brand has not switched on (warehouse ops, B2B, a single-warehouse rebalance) is absent
    // outright, exactly as before, whatever the plan says: a switched-off surface is not a
    // thing to upsell. Of what remains, a locked item stays visible under "Premium".
    const lockedModules = orgConfig?.lockedModules;
    const visibleGroups = NAV_GROUPS.map((group) => {
        const items: MenuItem[] = [];
        const premium: MenuItem[] = [];
        for (const item of group.items) {
            if (item.path === '/rebalance' && !canRebalance) continue;
            if (item.path.startsWith('/warehouse-ops') && !warehouseOpsEnabled) continue;
            if (item.path.startsWith('/b2b') && !orgConfig?.b2bEnabled) continue;
            if (isModuleLocked(lockedModules, item.module)) {
                premium.push(item);
                continue;
            }
            items.push(item);
        }
        return { ...group, items, premium };
    }).filter((group) => group.items.length + group.premium.length > 0);

    // Active view for query-scoped items (e.g. /distribution?view=plan|track). Defaults to 'plan'.
    const currentView = new URLSearchParams(location.search).get('view');
    const isItemActive = (item: MenuItem, isChild = false): boolean => {
        const [path, query] = item.path.split('?');
        // Under B2B Customers: Price lists & schemes owns /b2b/pricing, Customers every other B2B page.
        if (isChild && path.startsWith('/b2b/')) {
            const pricing = currentPath.startsWith('/b2b/pricing');
            return path === '/b2b/pricing' ? pricing : currentPath.startsWith('/b2b/') && !pricing;
        }
        // Every B2B page (a customer, price lists & schemes, a new order) sits under B2B Customers.
        if (path === '/b2b/customers' && currentPath.startsWith('/b2b/')) return true;
        if (path !== currentPath) return false;
        if (!query) return true;
        const wantView = new URLSearchParams(query).get('view');
        return wantView === (currentView ?? 'plan');
    };

    const renderPremiumItem = (item: MenuItem) => {
        const Icon = item.icon;
        const spacing = collapsed ? 'justify-center px-0 py-1.5' : 'justify-start px-2.5 py-1.5';
        return (
            <HoverTooltip key={item.path} label={PREMIUM_TOOLTIP} position="right">
                <button
                    type="button"
                    aria-disabled="true"
                    className={`flex w-full cursor-not-allowed items-center rounded-full text-[11px] text-slate-400 ${spacing}`}
                >
                    {Icon ? (
                        <Icon size={16} weight="regular" className="shrink-0 text-slate-400" />
                    ) : (
                        <span className="ml-1 h-1 w-1 shrink-0 rounded-full bg-slate-300" />
                    )}
                    {!collapsed && (
                        <>
                            <span className="ml-2 whitespace-nowrap">{item.label}</span>
                            <Lock size={11} weight="fill" className="ml-auto shrink-0 text-slate-400" />
                        </>
                    )}
                </button>
            </HoverTooltip>
        );
    };

    const renderItem = (item: MenuItem, isChild = false) => {
        const Icon = item.icon;
        const isActive = isItemActive(item, isChild);

        const base =
            'flex w-full items-center rounded-full text-[11px] transition-colors duration-150';
        const spacing = collapsed
            ? 'justify-center px-0 py-1.5'
            : isChild
            ? 'justify-start pl-8 pr-2.5 py-1'
            : 'justify-start px-2.5 py-1.5';

        const state = isActive
            ? 'font-semibold text-slate-900'
            : 'text-slate-700';

        const buttonElement = (
            <button
                key={item.path}
                type="button"
                onClick={() => navigate(item.path)}
                className={`${base} ${spacing} ${state} cursor-pointer hover:bg-gray-300`}
                style={{
                    backgroundColor: isActive ? ACTIVE_BG : 'transparent',
                }}
            >
                {Icon ? (
                    <Icon
                        size={16}
                        weight={isActive ? 'fill' : 'regular'}
                        className={`shrink-0 ${
                            isActive ? 'text-slate-900' : 'text-slate-600'
                        }`}
                    />
                ) : (
                    <span
                        className={`ml-1 h-1 w-1 shrink-0 rounded-full ${
                            isActive ? 'bg-slate-900' : 'bg-slate-400'
                        }`}
                    />
                )}
                {!collapsed && (
                    <span
                        className={`ml-2 whitespace-nowrap transition-all duration-150 ${
                            collapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'
                        }`}
                    >
                        {item.label}
                    </span>
                )}
            </button>
        );

        // Show tooltip only when collapsed
        if (collapsed) {
            return (
                <HoverTooltip
                    key={item.path}
                    label={item.label}
                    position="right"
                >
                    {buttonElement}
                </HoverTooltip>
            );
        }

        return buttonElement;
    };

    return (
        <Stack
            h="100%"
            p="sm"
            gap="sm"
            style={{
                boxSizing: 'border-box',
                justifyContent: 'space-between',
                width: '100%',
                backgroundColor: SIDEBAR_BG, // 1. light grey bg
            }}
        >
            {/* TOP: Reparo + collapse icon */}
            <Box>
                {/* header layout: row when open, column when collapsed */}
                <div
                    className={`rounded-md px-2 py-2`}
                    style={{ backgroundColor: SIDEBAR_BG }}
                >
                    {collapsed ? (
                        // collapsed: R + circle stacked vertically
                        <div className="flex flex-col items-center gap-2">
                            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-slate-900 text-[10px] font-semibold text-white">
                                R
                            </div>
                            <button
                                type="button"
                                onClick={toggleSidebar}
                                className="flex h-8 w-8 items-center justify-center rounded-full bg-white/60 text-slate-700 hover:bg-white hover:text-slate-900"
                            >
                                <CaretRight size={16} weight="bold" />
                            </button>
                        </div>
                    ) : (
                        // expanded: R + "Reparo" + circle button in one row
                        <div className="flex items-center justify-between">
                            <div className="flex min-w-0 items-center gap-2">
                                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-slate-900 text-[10px] font-semibold text-white">
                                    R
                                </div>
                                <div className="flex min-w-0 flex-col leading-tight">
                                    <span className="text-[12px] font-medium text-slate-900">
                                        {COMPANY_NAME}
                                    </span>
                                </div>
                            </div>

                            {/* 5: circular button, icon centered */}
                            <button
                                type="button"
                                onClick={toggleSidebar}
                                className="flex h-8 w-8 items-center justify-center rounded-full bg-white/60 text-slate-700 hover:bg-white hover:text-slate-900"
                            >
                                <CaretLeft size={16} weight="bold" />
                            </button>
                        </div>
                    )}
                </div>
            </Box>

            {/* MAIN ITEMS */}
            <Box
                style={{ flex: 1, overflowY: 'auto' }}
                className={collapsed ? 'mt-2' : 'mt-1'}
            >
                <Stack gap={0}>
                    {visibleGroups.map((group, i) => (
                        <Box
                            key={group.label ?? '__home'}
                            mt={i === 0 ? 0 : collapsed ? 8 : 4}
                        >
                            {!collapsed && group.label && (
                                <Text
                                    size="xs"
                                    fw={600}
                                    className="px-1 pb-0.5 text-[10px] tracking-[0.12em] text-slate-500 uppercase"
                                    mt={i === 0 ? 0 : 'xs'}
                                >
                                    {group.label}
                                </Text>
                            )}
                            {collapsed && i > 0 && (
                                <div className="mx-auto mb-2 h-px w-5 bg-slate-300" />
                            )}
                            <Stack gap={3}>
                                {group.items.map((item) => (
                                    <React.Fragment key={item.path}>
                                        {renderItem(item)}
                                        {/* Sub-items open only while the section is active (e.g. B2B Customers). */}
                                        {!collapsed &&
                                            isItemActive(item) &&
                                            item.children &&
                                            item.children.length > 0 && (
                                                <Stack gap={2}>
                                                    {item.children.map((child) =>
                                                        renderItem(child, true),
                                                    )}
                                                </Stack>
                                            )}
                                    </React.Fragment>
                                ))}
                            </Stack>
                            {group.premium.length > 0 && (() => {
                                const key = group.label ?? '__home';
                                const open = !!premiumOpen[key];
                                const toggle = (
                                    <button
                                        type="button"
                                        onClick={() => togglePremium(key)}
                                        aria-expanded={open}
                                        className={`flex w-full items-center rounded-full text-[10px] text-slate-400 transition-colors duration-150 hover:bg-gray-300 hover:text-slate-600 ${
                                            collapsed ? 'justify-center px-0 py-1.5' : 'justify-start px-2.5 py-1'
                                        }`}
                                    >
                                        <Lock size={collapsed ? 14 : 11} weight="fill" className="shrink-0" />
                                        {!collapsed && (
                                            <>
                                                <span className="ml-2 tracking-[0.08em] uppercase">
                                                    {PREMIUM_GROUP_LABEL} · {group.premium.length}
                                                </span>
                                                {open ? (
                                                    <CaretDown size={10} weight="bold" className="ml-auto shrink-0" />
                                                ) : (
                                                    <CaretRight size={10} weight="bold" className="ml-auto shrink-0" />
                                                )}
                                            </>
                                        )}
                                    </button>
                                );
                                return (
                                    <Box mt={group.items.length > 0 ? 4 : 0}>
                                        {collapsed ? (
                                            <HoverTooltip label={`${PREMIUM_GROUP_LABEL} (${group.premium.length})`} position="right">
                                                {toggle}
                                            </HoverTooltip>
                                        ) : (
                                            toggle
                                        )}
                                        {open && (
                                            <Stack gap={3} mt={3}>
                                                {group.premium.map((item) => renderPremiumItem(item))}
                                            </Stack>
                                        )}
                                    </Box>
                                );
                            })()}
                        </Box>
                    ))}
                </Stack>
            </Box>

            {/* BOTTOM ITEMS */}
            <Box className="pb-1">
                <Stack gap={3}>{BOTTOM_ITEMS.map((item) => renderItem(item))}</Stack>

                <Menu shadow="md" width={200} position="right-end">
                    <Menu.Target>
                        <UnstyledButton
                            className={`flex w-full items-center rounded-md p-2 transition-colors hover:bg-gray-300 ${
                                collapsed ? 'justify-center' : 'justify-start'
                            }`}
                        >
                            <UserCircle
                                size={20}
                                weight="duotone"
                                className="shrink-0 text-slate-700"
                            />
                            {!collapsed && (
                                <Text
                                    ml="sm"
                                    className="truncate text-[11px] text-slate-700"
                                >
                                    {userFullName}
                                </Text>
                            )}
                        </UnstyledButton>
                    </Menu.Target>
                    <Menu.Dropdown>
                        <Menu.Item
                            leftSection={<SignOut size={16} />}
                            color="red"
                            onClick={() => {
                                // Clear auth data
                                localStorage.removeItem('token');
                                localStorage.removeItem('user');
                                // Clear merchant ID cache to ensure next user gets fresh data
                                clearMerchantIdCache();
                                // Clear AI chat thread cookies and agent session data to prevent cross-merchant persistence
                                clearAllAiThreadCookies();
                                clearAllAgentSessionStorage();
                                clearQuickHelpSessionStorage();
                                // Clear React Query cache to prevent stale user data
                                queryClient.clear();
                                navigate('/login');
                            }}
                        >
                            Logout
                        </Menu.Item>
                    </Menu.Dropdown>
                </Menu>
            </Box>
        </Stack>
    );
};

export default SideNavigationBar;
